import React, {useMemo, useRef} from 'react';
import ReactECharts from 'echarts-for-react';
import {Box, useTheme} from '@mui/material';
import type {EChartsOption} from 'echarts';
import {ChartLoadingAnimation} from './ChartLoadingAnimation';

type Props = {
    config: string;
}
export const ChartRenderer: React.FC<Props> = React.memo(({config}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const containerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<ReactECharts>(null);
    const chartOption = useMemo(() => {
        try {
            const parsed = JSON.parse(config) as EChartsOption;
            const series = Array.isArray(parsed.series)
                ? parsed.series
                : parsed.series
                    ? [parsed.series]
                    : [];
            const titleOption = parsed.title;
            const hasTitle = Boolean(
                titleOption &&
                (Array.isArray(titleOption)
                    ? titleOption.some((t) => t?.text)
                    : titleOption.text),
            );
            const legendOption = parsed.legend;
            const legendDisabled =
                legendOption != null &&
                !Array.isArray(legendOption) &&
                legendOption.show === false;
            const hasLegend = !legendDisabled && (
                (Array.isArray(legendOption)
                    ? legendOption.some((l) => l?.data?.length)
                    : legendOption?.data?.length) ||
                series.some((s) => s && typeof s === 'object' && 'name' in s && s.name)
            );
            const legendOnTop = hasLegend && (
                Array.isArray(legendOption)
                    ? legendOption.some((l) => l?.top != null && l?.bottom == null)
                    : (legendOption?.top === 'top' || legendOption?.bottom == 'top')
            );

            const defaultGrid: EChartsOption['grid'] = {
                left: '3%',
                right: '4%',
                bottom: hasLegend && !legendOnTop ? 48 : 16,
                containLabel: true,
                borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
            };

            if (!parsed.grid || !(Array.isArray(parsed.grid) ? parsed.grid[0]?.top : parsed.grid.top)) {
                let top = 16;
                if (hasTitle) top += 40;
                if (legendOnTop) top += 36;
                if(parsed.yAxis) top += 30; // needed for the y-axis title to be visible
                defaultGrid.top = top;
            }

            const defaultLegend = hasLegend
                ? {
                    textStyle: {
                        color: isDark ? '#ffffff' : '#1a1a1a',
                    },
                    bottom: 8,
                    left: 'center',
                    type: 'scroll' as const,
                }
                : undefined;

            // Apply theme-aware defaults
            const defaultOption: Partial<EChartsOption> = {
                backgroundColor: 'transparent',
                textStyle: {
                    color: isDark ? '#ffffff' : '#1a1a1a',
                },
                grid: defaultGrid,
                tooltip: {
                    backgroundColor: isDark ? 'rgba(26, 26, 26, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                    borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                    textStyle: {
                        color: isDark ? '#ffffff' : '#1a1a1a',
                    },
                    trigger: 'axis',
                    confine: true,
                    axisPointer: {
                        type: 'cross',
                        lineStyle: {
                            color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                        },
                    },
                },
                ...(defaultLegend ? {legend: defaultLegend} : {}),
            };

            // Deep merge the default options with parsed config
            const mergedOption: EChartsOption = {
                ...defaultOption,
                ...parsed,
                series:Array.isArray(parsed.series) ? parsed.series.map((s) => ({...s, center: ['50%', '50%']})) : {...parsed.series, center: ['50%', '50%']},
                textStyle: {
                    ...defaultOption.textStyle,
                    ...parsed.textStyle,
                },
                grid: {
                    ...defaultGrid,
                    ...(Array.isArray(parsed.grid) ? parsed.grid[0] : parsed.grid),
                },
                tooltip: {
                    ...defaultOption.tooltip,
                    ...(Array.isArray(parsed.tooltip) ? parsed.tooltip[0] : parsed.tooltip),
                    confine: (Array.isArray(parsed.tooltip) ? parsed.tooltip[0]?.confine : parsed.tooltip?.confine) ?? true,
                },
                ...(hasLegend
                    ? {
                        legend: {
                            ...defaultLegend,
                            ...(Array.isArray(legendOption) ? legendOption[0] : legendOption),
                            orient: 'horizontal',
                        },
                    }
                    : {}),
            };

            return mergedOption;
        } catch (error) {
            console.error('Error parsing chart configuration:', error);
        }
    }, [config, isDark]);

    // cannot show loading and error state separately, because it keeps getting JSON parse error until get it as a valid JSON.
    if (!chartOption) {
        return <ChartLoadingAnimation/>;
    }

    return (
        <Box
            ref={containerRef}
            sx={{
                p: 2,
                width: '100%',
                minHeight: 360,
                height: 'clamp(360px, 45vw, 516px)',
                my: 2,
                border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                borderRadius: 2,
                backgroundColor: isDark ? 'rgba(26, 26, 26, 0.5)' : 'rgba(248, 250, 252, 0.5)',
                overflow: 'visible',
                position: 'relative',
            }}
        >
            <ReactECharts
                ref={chartRef}
                option={chartOption}
                style={{
                    height: '100%',
                    width: '100%',
                }}
                opts={{
                    renderer: 'svg',
                }}
                autoResize
            />
        </Box>
    );
});
